import { ApprovalRounded, ConstructionRounded, Diversity3Rounded, NewReleasesRounded, PermMedia, ReviewsRounded, ViewQuiltRounded } from '@mui/icons-material'
import { Button, Card, CardActionArea, CardContent, Divider, Grid, List, ListItem, ListItemText, Skeleton, Stack, Typography } from '@mui/material'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { IContract, IContractsStatusAnalytics } from '../../definitions/Contract'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'

interface TypeDistributionProps {
    programType: string
    planMarkets: Record<string, number>
}

const TypeDistributionBlock = ({ programType, planMarkets }: TypeDistributionProps) => {
    const total = Object.keys(planMarkets).includes('TOTAL') ? planMarkets?.['TOTAL'] : Object.values(planMarkets).reduce((acc, el) => acc + el)
    return (
        <List subheader={(
            <Stack direction='row' justifyContent='space-between'>
                <Typography variant='subtitle1'>{programType}</Typography>
                <Typography variant='subtitle1'>
                    {total}
                </Typography>
            </Stack>
        )}>
            <List component='div' disablePadding>
                {
                    Object.entries(planMarkets).filter(item => item[0] !== 'TOTAL')
                        .map(([planMarket, totalSum]) => (
                            <ListItem key={planMarket} sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }} disablePadding>
                                <ListItemText secondary={planMarket} />
                                <Typography variant='body2' color='text.secondary'>
                                    {totalSum}
                                </Typography>
                            </ListItem>
                        ))
                }
            </List>
        </List>
    )
}

const displayContractsCategories = (status: string, contracts: IContract[]) => {
    if (status === 'loading') return (
        <Stack justifyContent='space-between' direction='row' width='100%'>
            <Skeleton variant='text' width='80px' />
            <Skeleton variant='text' width='40px' />
        </Stack>
    )
    if (contracts?.length < 1 && status !== 'loading') return (
        <Stack justifyContent='center' alignItems='center'>
            <Typography>No data available</Typography>
        </Stack>
    )

    const categories = contracts?.reduce((acc, item) => {
        const productType = item.productType.name
        const planMarket = item.type.name
        if (productType && planMarket) {
            if (!acc[planMarket]) {
                acc[planMarket] = {}
            }
            acc[planMarket][productType] = (acc[planMarket][productType] || 0) + 1
        }
        return acc
    }, {} as Record<string, Record<string, number>>)

    return (
        <Stack sx={{ width: '100%' }}>
            {
                Object.entries(categories).map(([programType, planMarkets]) => {
                    return <TypeDistributionBlock key={programType} planMarkets={planMarkets} programType={programType} />
                })
            }
        </Stack>
    )
}

const displayMembersCategories = (status: string, members: IContractsStatusAnalytics['members']) => {
    if (status === 'loading') return (
        <Stack justifyContent='space-between' direction='row' width='100%'>
            <Skeleton variant='text' width='80px' />
            <Skeleton variant='text' width='40px' />
        </Stack>
    )
    if (members?.breakdown?.length < 1 && status !== 'loading') return (
        <Stack justifyContent='center' alignItems='center'>
            <Typography>No data available</Typography>
        </Stack>
    )

    const groupedData = members.breakdown.reduce((acc, item) => {
        const { planMarket, programType, sum } = item

        if (!acc[programType]) {
            acc[programType] = {}
        }

        if (!acc[programType][planMarket]) {
            acc[programType][planMarket] = 0
        }

        acc[programType][planMarket] += sum

        return acc
    }, {} as Record<string, Record<string, number>>)

    return (
        <Stack sx={{ width: '100%' }}>
            {
                Object.entries(groupedData).map(([programType, planMarkets]) => {
                    return <TypeDistributionBlock key={programType} planMarkets={planMarkets} programType={programType} />
                })
            }
        </Stack>
    )
}

const Administrator = () => {
    const dispatch = useAppDispatch()
    const { status, response } = useQuery({
        queryKey: buildQueryKey(API_URLS.os.getContractsStatusAnalytics),
        queryFn: () => ApiClient.getContractsStatusAnalytics(),
        cache: false
    })

    const underDev = () => {
        snackbar.show({
            severity: 'info',
            text: 'This functionality will be developed in future.'
        })
    }

    const uniqueMembersLive = response?.members?.total

    return (
        <Stack spacing={3}>
            <Stack direction='row' justifyContent='space-between'>
                <Typography variant='h5'>
                    <strong>Admin&apos;s Hub</strong>
                </Typography>
                <Stack direction='row' spacing={1}>
                    <Button onClick={underDev} variant='outlined'>View Tickets</Button>
                    <Button onClick={underDev} startIcon={<NewReleasesRounded />} variant='contained'>Open Ticket</Button>
                </Stack>
            </Stack>

            <SmartCard>
                <Stack spacing={2}>
                    <Stack direction='row' spacing={1}>
                        <ConstructionRounded fontSize='large' />
                        <Typography variant='h5' fontWeight='bold'>Admin&apos;s tools</Typography>
                    </Stack>
                    <Divider />
                    <Stack display='grid' gridTemplateColumns='repeat(auto-fill, minmax(20%, 1fr))' gap={2} direction='row'>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'tooltip-admin' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <ReviewsRounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Tooltips Manager</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all the tooltips and help messages
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'contracts', params: { tab: 'schemas' } }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <ViewQuiltRounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Contract Schema Editor</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Edit the templates to build contracts&apos; structures
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'media' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <PermMedia fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Media Manager</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all media types like PDFs, Images, Documents...
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'approvals' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <ApprovalRounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Approvals Hub</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all approval requests for new contract, report...
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'groups-management' }))}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <Diversity3Rounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>Approval Groups</Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all the approval groups, add, remove or edit infos
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} height='100%'>
                            <Card sx={{ height: '100%' }}>
                                <CardActionArea onClick={() => dispatch(changeRoute({ route: 'query-creation' }))} sx={{ height: '100%' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <Stack spacing={2}>
                                            <Diversity3Rounded fontSize='large' color='primary' />
                                            <Stack>
                                                <Typography variant='body1'>
                                                    Query Creation
                                                </Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Manage all the queries
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>

                    </Stack>
                </Stack>
            </SmartCard>

            <Stack gap={2} marginTop={2}>
                <Stack gap={2}>
                    <Typography variant='h6'>Analytics</Typography>
                    <Grid container spacing={2}>
                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Typography variant='body1' pb={2}>Live Contracts</Typography>
                                <Typography variant='h5'>
                                    {
                                        status === 'loading' ? <Skeleton variant='text' /> : response?.contracts?.live?.length
                                    }
                                </Typography>
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Typography variant='body1' pb={2}>Draft Contracts</Typography>
                                <Typography variant='h5'>
                                    {
                                        status === 'loading' ? <Skeleton variant='text' /> : response?.contracts?.draft?.length
                                    }
                                </Typography>
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Typography variant='body1' pb={2}>Archived Contracts</Typography>
                                <Typography variant='h5'>
                                    {
                                        status === 'loading' ? <Skeleton variant='text' /> : response?.contracts?.archived?.length
                                    }
                                </Typography>
                            </SmartCard>
                        </Grid>
                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Typography variant='body1' pb={2}>Unique members live</Typography>
                                <Typography variant='h5'>
                                    {
                                        status === 'loading' ? <Skeleton variant='text' /> : uniqueMembersLive
                                    }
                                </Typography>
                            </SmartCard>
                        </Grid>

                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Stack direction='row' justifyContent='space-between'>
                                    {displayContractsCategories(status, response?.contracts?.live)}
                                </Stack>
                            </SmartCard>
                        </Grid>

                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Stack direction='row' justifyContent='space-between'>
                                    {displayContractsCategories(status, response?.contracts?.draft)}
                                </Stack>
                            </SmartCard>
                        </Grid>

                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Stack direction='row' justifyContent='space-between'>
                                    {displayContractsCategories(status, response?.contracts?.archived)}
                                </Stack>
                            </SmartCard>
                        </Grid>

                        <Grid item md={3}>
                            <SmartCard style={{ border: '0' }}>
                                <Stack direction='row' justifyContent='space-between'>
                                    {displayMembersCategories(status, response?.members)}
                                </Stack>
                            </SmartCard>
                        </Grid>
                    </Grid>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default Administrator
